import React, { Component } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Popover,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { publicEncrypt } from 'crypto';
import { get, post } from '../../commons/http';
import Side from '../../components/side';
import styles from './style.less';

const { Option } = Select;

const ROLE_MAP = {
  student: { text: '学生', color: 'blue' },
  teacher: { text: '老师', color: 'green' },
  admin: { text: '管理员', color: 'red' },
};

// 管理页侧边栏（含返回竞赛列表）
const SIDE_MENUS = [
  {
    key: 'competition',
    text: '竞赛',
    icon: 'book',
    path: '/home/competition/all',
  },
  {
    key: 'userManage',
    text: '用户管理',
    icon: 'team',
    path: '/home/user-manage',
  },
  {
    key: 'portal',
    text: '返回官网',
    icon: 'home',
    path: '/',
    external: true,
  },
];

/**
 * 用户管理（仅管理员可见）
 * 复用 admin 后端接口：/user/all/info、/user/updateInfo、/user/delete、/user/add
 */
export default class UserManage extends Component {
  state = {
    users: [],
    groupMap: {}, // userId -> groups[]
    loading: true,
    modalVisible: false,
    editing: null, // null=新增，否则为编辑中的用户
    publicKey: '',
    submitting: false,
  };

  formRef = React.createRef();

  componentDidMount() {
    this.fetchUsers();
    this.fetchGroupInfo();
    this.fetchPublicKey();
  }

  fetchGroupInfo = async () => {
    try {
      const res = await get('/user/groupInfo');
      if (res.code === 0) {
        const groupMap = {};
        (res.data.users || []).forEach((u) => {
          groupMap[u._id] = u.groups || [];
        });
        this.setState({ groupMap });
      }
    } catch (e) {}
  };

  fetchUsers = async () => {
    this.setState({ loading: true });
    try {
      const res = await get('/user/all/info');
      if (res.code === 0) {
        this.setState({ users: res.data || [], loading: false });
      } else {
        this.setState({ loading: false });
      }
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  fetchPublicKey = async () => {
    let publicKey = window.localStorage.getItem('publicKey');
    if (!publicKey) {
      const res = await get('/user/key');
      if (res.code === 0) {
        publicKey = res.data.key;
        window.localStorage.setItem('publicKey', publicKey);
      }
    }
    this.setState({ publicKey });
  };

  encryptPassword = (pwd) => {
    if (!pwd) return undefined;
    return publicEncrypt(this.state.publicKey, Buffer.from(pwd)).toString(
      'base64'
    );
  };

  openAdd = () => {
    this.setState({ editing: null, modalVisible: true }, () => {
      this.formRef.current && this.formRef.current.resetFields();
    });
  };

  openEdit = (user) => {
    this.setState({ editing: user, modalVisible: true }, () => {
      this.formRef.current &&
        this.formRef.current.setFieldsValue({
          email: user.email,
          name: user.name,
          staffId: user.staffId,
          role: user.role,
          password: undefined,
        });
    });
  };

  handleDelete = async (user) => {
    const res = await post('/user/delete', { id: user._id });
    if (res.code === 0) {
      message.success('已删除', 1.5);
      this.fetchUsers();
    } else {
      message.error(res.msg || '删除失败', 2);
    }
  };

  handleSubmit = async () => {
    const { editing, publicKey } = this.state;
    if (!publicKey) {
      message.warning('公钥加载中，请稍候', 1.5);
      return;
    }
    try {
      const values = await this.formRef.current.validateFields();
      this.setState({ submitting: true });
      const payload = {
        email: values.email,
        name: values.name,
        staffId: values.staffId,
        role: values.role,
      };
      if (values.password) {
        payload.password = this.encryptPassword(values.password);
      }

      let res;
      if (editing) {
        res = await post('/user/updateInfo', { ...payload, _id: editing._id });
      } else {
        if (!values.password) {
          message.warning('新增用户必须设置密码', 2);
          this.setState({ submitting: false });
          return;
        }
        res = await post('/user/add', payload);
      }

      this.setState({ submitting: false });
      if (res.code === 0) {
        message.success(editing ? '更新成功' : '新增成功', 1.5);
        this.setState({ modalVisible: false });
        this.fetchUsers();
      } else {
        message.error(res.msg || '操作失败', 2);
      }
    } catch (e) {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { users, loading, modalVisible, editing, submitting } = this.state;
    const currentUserId = JSON.parse(
      window.localStorage.getItem('user') || '{}'
    ).id;

    const columns = [
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: '邮箱',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: '学号/工号',
        dataIndex: 'staffId',
        key: 'staffId',
      },
      {
        title: '角色',
        dataIndex: 'role',
        key: 'role',
        render: (role) => {
          const r = ROLE_MAP[role] || { text: role, color: 'default' };
          return <Tag color={r.color}>{r.text}</Tag>;
        },
      },
      {
        title: '分组情况',
        key: 'groups',
        width: 200,
        render: (_, record) => {
          const groups = this.state.groupMap[record._id] || [];
          if (groups.length === 0) {
            return <span style={{ color: '#bbb' }}>未报名</span>;
          }
          return (
            <Popover
              placement="left"
              title={`${record.name} 的分组详情`}
              content={
                <div style={{ maxWidth: 360 }}>
                  {groups.map((g, i) => (
                    <div key={i} style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {g.competitionTitle}{' '}
                        {g.isCaptain ? (
                          <Tag color="gold">队长</Tag>
                        ) : (
                          <Tag>队员</Tag>
                        )}
                        {g.score !== null && g.score !== undefined && (
                          <Tag color="blue">得分 {g.score}</Tag>
                        )}
                      </div>
                      {g.inviteCode && (
                        <div style={{ fontSize: 12, color: '#888' }}>
                          邀请码：{g.inviteCode}
                        </div>
                      )}
                      {g.members.length > 0 && (
                        <div style={{ fontSize: 12, color: '#666' }}>
                          队伍成员（{g.members.length}）：
                          {g.members
                            .map(
                              (m) =>
                                `${m.name}${m.isCaptain ? '(队长)' : ''}`
                            )
                            .join('、')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              }
            >
              <Button type="link" size="small" icon={<TeamOutlined />}>
                {groups.length} 个赛事
              </Button>
            </Popover>
          );
        },
      },
      {
        title: '操作',
        key: 'action',
        width: 180,
        render: (_, record) => (
          <span>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => this.openEdit(record)}
            >
              编辑
            </Button>
            {record._id !== currentUserId && (
              <Popconfirm
                title="确定删除该用户吗？"
                okText="删除"
                cancelText="取消"
                onConfirm={() => this.handleDelete(record)}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            )}
          </span>
        ),
      },
    ];

    return (
      <div>
        <Side sideWidth={210} menus={SIDE_MENUS} style={{ top: '55px' }} />
        <div className={styles.container} style={{ marginLeft: 230 }}>
        <div className={styles.header}>
          <h2 className={styles.title}>用户管理</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={this.openAdd}
          >
            新增用户
          </Button>
        </div>

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ pageSize: 15, showTotal: (t) => `共 ${t} 人` }}
        />

        <Modal
          title={editing ? '编辑用户' : '新增用户'}
          visible={modalVisible}
          onOk={this.handleSubmit}
          onCancel={() => this.setState({ modalVisible: false })}
          confirmLoading={submitting}
          okText="保存"
          cancelText="取消"
          destroyOnClose
        >
          <Form ref={this.formRef} layout="vertical">
            <Form.Item
              name="email"
              label="邮箱"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '邮箱格式不正确' },
              ]}
            >
              <Input placeholder="登录邮箱" disabled={!!editing} />
            </Form.Item>
            <Form.Item
              name="name"
              label="姓名"
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input placeholder="姓名" />
            </Form.Item>
            <Form.Item
              name="staffId"
              label="学号/工号"
              rules={[{ required: true, message: '请输入学号/工号' }]}
            >
              <Input placeholder="学号或工号" />
            </Form.Item>
            <Form.Item
              name="role"
              label="角色"
              rules={[{ required: true, message: '请选择角色' }]}
            >
              <Select placeholder="选择角色">
                <Option value="student">学生</Option>
                <Option value="teacher">老师</Option>
                <Option value="admin">管理员</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="password"
              label={editing ? '重置密码（留空则不修改）' : '初始密码'}
              rules={
                editing
                  ? []
                  : [{ required: true, message: '请设置初始密码' }]
              }
            >
              <Input.Password placeholder={editing ? '留空保持不变' : '初始密码'} />
            </Form.Item>
          </Form>
        </Modal>
        </div>
      </div>
    );
  }
}
